import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Save, ArrowLeft, Image as ImageIcon, Upload, X, GripVertical, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_TYPES = [
  { type: 'paragraph', label: 'Paragraph', labelAr: 'فقرة' },
  { type: 'heading', label: 'Heading', labelAr: 'عنوان' },
  { type: 'stat', label: 'Stat / KPI', labelAr: 'إحصائية' },
  { type: 'cta', label: 'CTA Box', labelAr: 'دعوة لإجراء' },
  { type: 'numbered_list', label: 'Numbered List', labelAr: 'قائمة مرقمة' },
  { type: 'table', label: 'Table', labelAr: 'جدول' },
];

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80);

const emptyArticle = () => ({
  slug: '', title_en: '', title_ar: '', category_en: 'Insights', category_ar: 'رؤى',
  excerpt_en: '', excerpt_ar: '', seo_title_en: '', seo_title_ar: '',
  meta_desc_en: '', meta_desc_ar: '', image: '', read_time: 5,
  executive_summary_en: '', executive_summary_ar: '',
  content_en: [{ type: 'paragraph', text: '' }], content_ar: [{ type: 'paragraph', text: '' }],
  faqs_en: [], faqs_ar: [], seo_keywords: [], author: '', status: 'published',
});

export default function BlogEditor({ isAr }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view; {} = new; existing article = edit
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/blog/articles`);
      setList(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openNew = useCallback(() => setEditing(emptyArticle()), []);
  const openEdit = useCallback((a) => {
    setEditing({ ...a, content_en: [...(a.content_en || [])], content_ar: [...(a.content_ar || [])], faqs_en: [...(a.faqs_en || [])], faqs_ar: [...(a.faqs_ar || [])], seo_keywords: [...(a.seo_keywords || [])] });
  }, []);
  const closeEditor = useCallback(() => { setEditing(null); setMsg(''); loadList(); }, [loadList]);

  const save = async () => {
    if (!editing.title_en || !editing.title_ar) { setMsg(isAr ? 'العنوان مطلوب بالعربية والإنجليزية' : 'Title required (EN & AR)'); return; }
    const slug = editing.slug || slugify(editing.title_en);
    const payload = { ...editing, slug };
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/admin/blog/articles/${slug}`, payload, { withCredentials: true });
      setMsg(isAr ? 'تم الحفظ ✓' : 'Saved ✓');
      setTimeout(closeEditor, 700);
    } catch (e) {
      setMsg((isAr ? 'فشل: ' : 'Failed: ') + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(isAr ? 'حذف هذا المقال نهائياً؟' : 'Delete this article permanently?')) return;
    try {
      await axios.delete(`${API}/admin/blog/articles/${slug}`, { withCredentials: true });
      loadList();
    } catch { /* ignore */ }
  };

  if (editing) return <Editor article={editing} setArticle={setEditing} onCancel={closeEditor} onSave={save} saving={saving} msg={msg} isAr={isAr} />;

  return (
    <div data-testid="admin-blog-list" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-[#0B2F44]">{isAr ? 'إدارة المدونة' : 'Blog Manager'}</h3>
          <p className="text-xs text-[#4A6572] mt-0.5">{isAr ? 'أنشئ ونشر مقالات SEO احترافية بدون كود' : 'Create and publish SEO articles with no code'}</p>
        </div>
        <Button data-testid="admin-blog-new" onClick={openNew} className="bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
          <Plus className="w-3.5 h-3.5 me-1.5" />{isAr ? 'مقال جديد' : 'New Article'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {list.map((a, i) => (
          <div key={a.slug} data-testid={`admin-blog-row-${i}`} className="bg-white rounded-xl border border-[#D0D8DC]/40 p-4 flex gap-3 hover:shadow-sm transition">
            <div className="w-20 h-20 rounded-lg bg-[#F8FAFB] overflow-hidden shrink-0 flex items-center justify-center">
              {a.image ? <img src={a.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${a.image}` : a.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <FileText className="w-6 h-6 text-[#A68966]/40" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-bold text-[#0B2F44] line-clamp-2 leading-tight">{isAr ? a.title_ar : a.title_en}</h4>
                <Badge className="bg-[#A68966]/10 text-[#A68966] border-0 text-[9px] uppercase tracking-wider shrink-0">{isAr ? a.category_ar : a.category_en}</Badge>
              </div>
              <p className="text-[11px] text-[#4A6572] mt-1 line-clamp-1">/{a.slug}</p>
              <div className="flex items-center gap-2 mt-2">
                <Button onClick={() => openEdit(a)} size="sm" variant="outline" className="h-7 text-[11px] px-2.5 border-[#D0D8DC]" data-testid={`admin-blog-edit-${i}`}>{isAr ? 'تعديل' : 'Edit'}</Button>
                <Button onClick={() => remove(a.slug)} size="sm" variant="outline" className="h-7 text-[11px] px-2.5 border-red-200 text-red-600 hover:bg-red-50" data-testid={`admin-blog-delete-${i}`}><Trash2 className="w-3 h-3" /></Button>
                <span className="text-[10px] text-[#4A6572] ms-auto">{a.published_at}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Editor({ article, setArticle, onCancel, onSave, saving, msg, isAr }) {
  const [lang, setLang] = useState('en');
  // Functional updater to avoid stale-closure when multiple state updates fire in the same tick
  const patchArticle = (patch) => setArticle((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  const set = (k, v) => patchArticle({ [k]: v });

  const uploadImage = async (file, onUrl) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await axios.post(`${API}/admin/upload-image`, { data: reader.result, filename: file.name }, { withCredentials: true });
        onUrl(data.url);
      } catch (e) { alert((isAr ? 'فشل الرفع: ' : 'Upload failed: ') + (e.response?.data?.detail || e.message)); }
    };
    reader.readAsDataURL(file);
  };

  const onCoverPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    uploadImage(f, (url) => set('image', url));
  };

  const contentKey = lang === 'en' ? 'content_en' : 'content_ar';
  const faqsKey = lang === 'en' ? 'faqs_en' : 'faqs_ar';
  const blocks = article[contentKey] || [];

  const addBlock = (type) => {
    const block = { type };
    if (type === 'paragraph' || type === 'heading') block.text = '';
    if (type === 'stat') { block.value = ''; block.label = ''; }
    if (type === 'cta') { block.text = ''; block.cta_type = 'consultation'; }
    if (type === 'numbered_list') block.items = [''];
    if (type === 'table') { block.headers = ['Column 1', 'Column 2']; block.rows = [['', '']]; }
    patchArticle((prev) => ({ [contentKey]: [...(prev[contentKey] || []), block] }));
  };
  const updateBlock = (i, patch) => patchArticle((prev) => ({ [contentKey]: (prev[contentKey] || []).map((b, idx) => idx === i ? { ...b, ...patch } : b) }));
  const removeBlock = (i) => patchArticle((prev) => ({ [contentKey]: (prev[contentKey] || []).filter((_, idx) => idx !== i) }));
  const moveBlock = (i, dir) => patchArticle((prev) => {
    const list = [...(prev[contentKey] || [])]; const t = i + dir;
    if (t < 0 || t >= list.length) return {};
    [list[i], list[t]] = [list[t], list[i]];
    return { [contentKey]: list };
  });

  const addFAQ = () => patchArticle((prev) => ({ [faqsKey]: [...(prev[faqsKey] || []), { question: '', answer: '' }] }));
  const updateFAQ = (i, patch) => patchArticle((prev) => ({ [faqsKey]: (prev[faqsKey] || []).map((f, idx) => idx === i ? { ...f, ...patch } : f) }));
  const removeFAQ = (i) => patchArticle((prev) => ({ [faqsKey]: (prev[faqsKey] || []).filter((_, idx) => idx !== i) }));

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <div data-testid="admin-blog-editor" className="space-y-4">
      {/* Sticky header */}
      <div className="sticky top-16 z-20 bg-[#F8FAFB]/95 backdrop-blur-md py-3 -mx-2 px-2 border-b border-[#D0D8DC]/40 flex items-center gap-2">
        <Button onClick={onCancel} size="sm" variant="outline" className="border-[#D0D8DC]" data-testid="admin-blog-back"><ArrowLeft className="w-3.5 h-3.5 me-1.5" />{isAr ? 'رجوع' : 'Back'}</Button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#4A6572]">{article.slug ? `/blog/${article.slug}` : (isAr ? 'مقال جديد' : 'New Article')}</p>
        </div>
        {msg && <span className="text-xs text-[#4A6572]">{msg}</span>}
        <div className="flex rounded-lg overflow-hidden border border-[#D0D8DC]">
          <button onClick={() => setLang('en')} className={`px-3 py-1.5 text-xs font-medium ${lang === 'en' ? 'bg-[#0B2F44] text-white' : 'bg-white text-[#4A6572]'}`} data-testid="admin-blog-lang-en">EN</button>
          <button onClick={() => setLang('ar')} className={`px-3 py-1.5 text-xs font-medium ${lang === 'ar' ? 'bg-[#0B2F44] text-white' : 'bg-white text-[#4A6572]'}`} data-testid="admin-blog-lang-ar">AR</button>
        </div>
        <Button onClick={onSave} disabled={saving} className="bg-[#A68966] hover:bg-[#947655] text-white text-sm" data-testid="admin-blog-save">
          <Save className="w-3.5 h-3.5 me-1.5" />{saving ? (isAr ? 'حفظ…' : 'Saving…') : (isAr ? 'حفظ المقال' : 'Save Article')}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Main content (2 cols) */}
        <div className="lg:col-span-2 space-y-4" dir={dir}>
          {/* Title */}
          <Field label={lang === 'ar' ? 'العنوان' : 'Title'}>
            <Input data-testid={`admin-blog-title-${lang}`} dir={dir} value={article[`title_${lang}`] || ''}
              onChange={(e) => {
                const v = e.target.value; const patch = { [`title_${lang}`]: v };
                if (lang === 'en' && !article.slug) patch.slug = slugify(v);
                setArticle({ ...article, ...patch });
              }}
              className="text-lg font-bold h-12 border-[#D0D8DC]" placeholder={lang === 'ar' ? 'عنوان المقال…' : 'Article title…'} />
          </Field>

          {/* Slug */}
          <Field label={isAr ? 'الرابط (slug)' : 'URL slug'}>
            <Input data-testid="admin-blog-slug" value={article.slug} onChange={(e) => set('slug', slugify(e.target.value))} className="h-9 border-[#D0D8DC] text-sm font-mono" placeholder="my-article-slug" />
          </Field>

          {/* Excerpt */}
          <Field label={lang === 'ar' ? 'المقتطف' : 'Excerpt'}>
            <Textarea dir={dir} value={article[`excerpt_${lang}`] || ''} onChange={(e) => set(`excerpt_${lang}`, e.target.value)} rows={2} className="border-[#D0D8DC] text-sm" />
          </Field>

          {/* Executive summary */}
          <Field label={lang === 'ar' ? 'الملخص التنفيذي (لـ GEO/AEO)' : 'Executive Summary (for GEO/AEO)'}>
            <Textarea data-testid={`admin-blog-summary-${lang}`} dir={dir} value={article[`executive_summary_${lang}`] || ''} onChange={(e) => set(`executive_summary_${lang}`, e.target.value)} rows={3} className="border-[#D0D8DC] text-sm" placeholder={lang === 'ar' ? 'إجابة مباشرة موجزة (40-60 كلمة) تظهر للذكاء الاصطناعي…' : 'Concise direct-answer (40-60 words) for AI overviews…'} />
          </Field>

          {/* Content Blocks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-[#0B2F44] uppercase tracking-wider">{lang === 'ar' ? 'بلوكات المحتوى' : 'Content Blocks'}</h4>
              <span className="text-[10px] text-[#4A6572]">{blocks.length} {lang === 'ar' ? 'بلوك' : 'blocks'}</span>
            </div>
            <div className="space-y-2">
              {blocks.map((b, i) => (
                <BlockEditor key={i} block={b} idx={i} dir={dir} isAr={isAr}
                  onChange={(patch) => updateBlock(i, patch)}
                  onRemove={() => removeBlock(i)}
                  onUp={() => moveBlock(i, -1)} onDown={() => moveBlock(i, 1)} />
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5 mt-3 p-2 bg-white rounded-lg border border-dashed border-[#D0D8DC]">
              <span className="text-[10px] text-[#4A6572] self-center me-1">+</span>
              {BLOCK_TYPES.map(t => (
                <Button key={t.type} onClick={() => addBlock(t.type)} size="sm" variant="outline" className="h-7 text-[11px] border-[#D0D8DC]" data-testid={`admin-blog-add-${t.type}`}>
                  {isAr ? t.labelAr : t.label}
                </Button>
              ))}
            </div>
          </div>

          {/* FAQs */}
          <div className="bg-white rounded-xl border border-[#D0D8DC]/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-[#0B2F44] uppercase tracking-wider">{lang === 'ar' ? 'الأسئلة الشائعة (FAQ Schema)' : 'FAQs (FAQ Schema)'}</h4>
              <Button onClick={addFAQ} size="sm" variant="outline" className="h-7 text-[11px] border-[#D0D8DC]" data-testid="admin-blog-add-faq"><Plus className="w-3 h-3 me-1" />{isAr ? 'سؤال' : 'FAQ'}</Button>
            </div>
            <div className="space-y-2">
              {(article[faqsKey] || []).map((f, i) => (
                <div key={i} className="border border-[#D0D8DC]/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[#A68966] font-bold">Q{i + 1}</span>
                    <button onClick={() => removeFAQ(i)} className="text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                  <Input dir={dir} value={f.question} onChange={(e) => updateFAQ(i, { question: e.target.value })} placeholder={lang === 'ar' ? 'السؤال' : 'Question'} className="h-9 text-sm border-[#D0D8DC]" />
                  <Textarea dir={dir} value={f.answer} onChange={(e) => updateFAQ(i, { answer: e.target.value })} placeholder={lang === 'ar' ? 'الإجابة' : 'Answer'} rows={3} className="text-sm border-[#D0D8DC]" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Sidebar */}
        <div className="space-y-4">
          {/* Cover */}
          <div className="bg-white rounded-xl border border-[#D0D8DC]/40 p-4">
            <label className="text-xs font-bold text-[#0B2F44] uppercase tracking-wider block mb-2">{isAr ? 'صورة الغلاف' : 'Cover Image'}</label>
            {article.image ? (
              <div className="relative">
                <img src={article.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${article.image}` : article.image} alt="" className="w-full h-32 object-cover rounded-lg" />
                <button onClick={() => set('image', '')} className="absolute top-1 end-1 bg-white/90 rounded-full p-1 shadow"><X className="w-3 h-3 text-red-600" /></button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-[#D0D8DC] rounded-lg p-6 text-center cursor-pointer hover:border-[#A68966] transition" data-testid="admin-blog-cover-upload">
                <Upload className="w-5 h-5 text-[#4A6572] mx-auto mb-1" />
                <span className="text-[11px] text-[#4A6572]">{isAr ? 'اضغط لرفع صورة' : 'Click to upload'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onCoverPick} />
              </label>
            )}
            <Input value={article.image} onChange={(e) => set('image', e.target.value)} placeholder={isAr ? 'أو الصق رابطاً' : 'or paste a URL'} className="mt-2 h-8 text-xs border-[#D0D8DC]" />
          </div>

          {/* Category & metadata */}
          <div className="bg-white rounded-xl border border-[#D0D8DC]/40 p-4 space-y-3">
            <Field tight label={lang === 'ar' ? 'التصنيف' : 'Category'}>
              <Input dir={dir} value={article[`category_${lang}`] || ''} onChange={(e) => set(`category_${lang}`, e.target.value)} className="h-9 text-sm border-[#D0D8DC]" />
            </Field>
            <Field tight label={isAr ? 'وقت القراءة (دقائق)' : 'Read time (min)'}>
              <Input type="number" min="1" value={article.read_time || 5} onChange={(e) => set('read_time', parseInt(e.target.value) || 5)} className="h-9 text-sm border-[#D0D8DC]" />
            </Field>
            <Field tight label={isAr ? 'الكاتب' : 'Author'}>
              <Input value={article.author || ''} onChange={(e) => set('author', e.target.value)} placeholder="GMRE Editorial" className="h-9 text-sm border-[#D0D8DC]" />
            </Field>
            <Field tight label={isAr ? 'الحالة' : 'Status'}>
              <Select value={article.status || 'published'} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-9 text-sm border-[#D0D8DC]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{isAr ? 'مسودة' : 'Draft'}</SelectItem>
                  <SelectItem value="published">{isAr ? 'منشور' : 'Published'}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* SEO */}
          <div className="bg-white rounded-xl border border-[#D0D8DC]/40 p-4 space-y-3">
            <h4 className="text-xs font-bold text-[#0B2F44] uppercase tracking-wider">SEO</h4>
            <Field tight label={lang === 'ar' ? 'عنوان SEO' : 'SEO title'}>
              <Input dir={dir} value={article[`seo_title_${lang}`] || ''} onChange={(e) => set(`seo_title_${lang}`, e.target.value)} className="h-9 text-sm border-[#D0D8DC]" maxLength={70} />
              <p className="text-[10px] text-[#4A6572] mt-1">{(article[`seo_title_${lang}`] || '').length}/70</p>
            </Field>
            <Field tight label={lang === 'ar' ? 'وصف Meta' : 'Meta description'}>
              <Textarea dir={dir} value={article[`meta_desc_${lang}`] || ''} onChange={(e) => set(`meta_desc_${lang}`, e.target.value)} rows={3} className="text-sm border-[#D0D8DC]" maxLength={160} />
              <p className="text-[10px] text-[#4A6572] mt-1">{(article[`meta_desc_${lang}`] || '').length}/160</p>
            </Field>
            <Field tight label={isAr ? 'الكلمات المفتاحية (مفصولة بفاصلة)' : 'Keywords (comma separated)'}>
              <Input value={(article.seo_keywords || []).join(', ')} onChange={(e) => set('seo_keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="h-9 text-sm border-[#D0D8DC]" />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children, tight }) => (
  <div className={tight ? '' : 'bg-white rounded-xl border border-[#D0D8DC]/40 p-4'}>
    <label className="text-[11px] text-[#4A6572] font-bold uppercase tracking-wider block mb-2">{label}</label>
    {children}
  </div>
);

function BlockEditor({ block, idx, dir, isAr, onChange, onRemove, onUp, onDown }) {
  return (
    <div data-testid={`admin-blog-block-${idx}`} className="bg-white rounded-lg border border-[#D0D8DC]/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <GripVertical className="w-3 h-3 text-[#4A6572]/40" />
          <span className="text-[10px] uppercase tracking-wider text-[#A68966] font-bold">{block.type.replace('_', ' ')}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onUp} className="w-6 h-6 rounded hover:bg-[#F8FAFB] text-[#4A6572] text-xs">↑</button>
          <button onClick={onDown} className="w-6 h-6 rounded hover:bg-[#F8FAFB] text-[#4A6572] text-xs">↓</button>
          <button onClick={onRemove} className="w-6 h-6 rounded hover:bg-red-50 text-red-500"><X className="w-3 h-3 m-auto" /></button>
        </div>
      </div>

      {(block.type === 'paragraph' || block.type === 'heading') && (
        <Textarea dir={dir} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} rows={block.type === 'heading' ? 1 : 4} className={`border-[#D0D8DC] ${block.type === 'heading' ? 'text-base font-bold' : 'text-sm'}`} placeholder={block.type === 'heading' ? (isAr ? 'عنوان فرعي' : 'Subheading') : (isAr ? 'نص الفقرة…' : 'Paragraph text…')} />
      )}

      {block.type === 'stat' && (
        <div className="grid grid-cols-3 gap-2">
          <Input value={block.value || ''} onChange={(e) => onChange({ value: e.target.value })} placeholder="47%" className="h-9 text-sm font-bold border-[#D0D8DC]" />
          <Input dir={dir} value={block.label || ''} onChange={(e) => onChange({ label: e.target.value })} placeholder={isAr ? 'الوصف' : 'Description'} className="h-9 text-sm border-[#D0D8DC] col-span-2" />
        </div>
      )}

      {block.type === 'cta' && (
        <div className="space-y-2">
          <Textarea dir={dir} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} rows={2} className="text-sm border-[#D0D8DC]" placeholder={isAr ? 'نص الدعوة' : 'CTA text'} />
          <Select value={block.cta_type || 'consultation'} onValueChange={(v) => onChange({ cta_type: v })}>
            <SelectTrigger className="h-8 text-xs border-[#D0D8DC]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="lead_magnet">Lead Magnet</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {block.type === 'numbered_list' && (
        <div className="space-y-1.5">
          {(block.items || []).map((item, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs text-[#4A6572] font-bold pt-2 w-5">{i + 1}.</span>
              <Textarea dir={dir} value={item} onChange={(e) => onChange({ items: block.items.map((it, ii) => ii === i ? e.target.value : it) })} rows={2} className="text-sm border-[#D0D8DC] flex-1" />
              <button onClick={() => onChange({ items: block.items.filter((_, ii) => ii !== i) })} className="text-red-500 self-start mt-2"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <Button onClick={() => onChange({ items: [...(block.items || []), ''] })} size="sm" variant="outline" className="h-7 text-[11px] border-[#D0D8DC]"><Plus className="w-3 h-3 me-1" />{isAr ? 'عنصر' : 'Item'}</Button>
        </div>
      )}

      {block.type === 'table' && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[#4A6572] uppercase tracking-wider">{isAr ? 'الأعمدة' : 'Columns'}:</span>
            {(block.headers || []).map((h, ci) => (
              <Input key={ci} dir={dir} value={h} onChange={(e) => onChange({ headers: block.headers.map((hh, ii) => ii === ci ? e.target.value : hh) })} className="h-7 text-xs border-[#D0D8DC]" />
            ))}
            <button onClick={() => onChange({ headers: [...block.headers, 'Col'], rows: block.rows.map(r => [...r, '']) })} className="text-xs text-[#A68966] font-bold">+</button>
            <button onClick={() => onChange({ headers: block.headers.slice(0, -1), rows: block.rows.map(r => r.slice(0, -1)) })} className="text-xs text-red-500 font-bold">−</button>
          </div>
          <div className="space-y-1">
            {(block.rows || []).map((row, ri) => (
              <div key={ri} className="flex gap-1.5 items-center">
                <span className="text-[10px] text-[#4A6572] w-4">{ri + 1}</span>
                {row.map((cell, ci) => (
                  <Input key={ci} dir={dir} value={cell} onChange={(e) => onChange({ rows: block.rows.map((rr, ii) => ii === ri ? rr.map((cc, jj) => jj === ci ? e.target.value : cc) : rr) })} className="h-7 text-xs border-[#D0D8DC]" />
                ))}
                <button onClick={() => onChange({ rows: block.rows.filter((_, ii) => ii !== ri) })} className="text-red-500"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <Button onClick={() => onChange({ rows: [...(block.rows || []), Array(block.headers.length).fill('')] })} size="sm" variant="outline" className="h-7 text-[11px] border-[#D0D8DC]"><Plus className="w-3 h-3 me-1" />{isAr ? 'صف' : 'Row'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
